import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { ArrowRight, Globe, FileText, Loader2, ClipboardPaste, Sparkles } from "lucide-react";

export default function Dashboard() {
  const [url, setUrl] = useState("");
  const [content, setContent] = useState("");
  const [keyword, setKeyword] = useState("");
  const [mode, setMode] = useState("url");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [recent, setRecent] = useState([]);
  const navigate = useNavigate();
  const [sp] = useSearchParams();

  useEffect(() => {
    const pending = localStorage.getItem("pending_url");
    const q = sp.get("url");
    if (q) setUrl(q);
    else if (pending) {
      setUrl(pending);
      localStorage.removeItem("pending_url");
    }
    api.get("/scans").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, [sp]);

  const runScan = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const payload = mode === "url"
        ? { url: url.trim(), target_keyword: keyword.trim() || null }
        : { content, target_keyword: keyword.trim() || null };
      const { data } = await api.post("/scans", payload);
      navigate(`/scan/${data.id}`);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white text-[#37352F] min-h-[calc(100vh-3.5rem)]">
      <div className="max-w-4xl mx-auto px-6 py-10 md:py-14">
        <div className="flex items-center gap-3 mb-2">
          <span className="notion-emoji-box callout-blue">📝</span>
          <h1 className="font-serif-h text-3xl md:text-4xl font-semibold tracking-tight">New scan</h1>
        </div>
        <p className="text-[#787774] mb-10 ml-[52px]">Enter a URL or paste content. We&apos;ll grade both SEO and GEO, then generate a step-by-step action plan with GPT-5.2.</p>

        <div className="border border-[#EBEBEA] rounded-lg bg-white overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-[#EBEBEA] px-2 pt-2 gap-1">
            <TabBtn active={mode === "url"} onClick={() => setMode("url")} testId="tab-url">
              <Globe className="w-4 h-4" /> Scan URL
            </TabBtn>
            <TabBtn active={mode === "content"} onClick={() => setMode("content")} testId="tab-content">
              <ClipboardPaste className="w-4 h-4" /> Paste content
            </TabBtn>
          </div>

          <form onSubmit={runScan} className="p-6 space-y-5">
            {mode === "url" ? (
              <div>
                <label className="text-xs font-medium text-[#787774] block mb-1.5">Website URL</label>
                <input
                  type="text"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/blog/post-1"
                  data-testid="scan-url-input"
                  required={mode === "url"}
                  className="w-full bg-white border border-[#EBEBEA] focus:border-[#2383E2] focus:ring-2 focus:ring-[#D4EBF8] rounded-md px-3.5 py-2.5 outline-none transition"
                />
              </div>
            ) : (
              <div>
                <label className="text-xs font-medium text-[#787774] block mb-1.5">HTML or content</label>
                <textarea
                  rows={10}
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="Paste full HTML or article text…"
                  data-testid="scan-content-input"
                  required={mode === "content"}
                  className="w-full bg-white border border-[#EBEBEA] focus:border-[#2383E2] focus:ring-2 focus:ring-[#D4EBF8] rounded-md px-3.5 py-2.5 text-sm outline-none transition font-mono"
                />
              </div>
            )}

            <div>
              <label className="text-xs font-medium text-[#787774] block mb-1.5">Target keyword <span className="text-[#9B9A97]">(optional)</span></label>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="e.g. best productivity tools 2026"
                data-testid="scan-keyword-input"
                className="w-full bg-white border border-[#EBEBEA] focus:border-[#2383E2] focus:ring-2 focus:ring-[#D4EBF8] rounded-md px-3.5 py-2.5 outline-none transition"
              />
            </div>

            {error && (
              <div className="callout-red rounded-md text-[#8B2A20] text-sm px-3.5 py-2.5" data-testid="scan-error">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              data-testid="run-scan-button"
              className="bg-[#2383E2] text-white font-medium px-5 py-2.5 rounded-md flex items-center gap-2 hover:bg-[#1A6FCC] transition-colors disabled:opacity-50"
            >
              {busy ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Analyzing (10–30s)…
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" /> Run scan <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>

        {/* Recent */}
        {recent.length > 0 && (
          <div className="mt-14">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <span className="notion-emoji-box callout-gray" style={{ width: 32, height: 32, fontSize: 18 }}>🕘</span>
                <h2 className="font-serif-h text-2xl font-semibold">Recent scans</h2>
              </div>
              <Link to="/history" data-testid="view-all-history-link" className="text-sm text-[#2383E2] hover:underline">View all →</Link>
            </div>
            <div className="border border-[#EBEBEA] rounded-lg divide-y divide-[#EBEBEA] overflow-hidden">
              {recent.map((s) => (
                <Link
                  key={s.id}
                  to={`/scan/${s.id}`}
                  data-testid={`recent-scan-${s.id}`}
                  className="hover:bg-[#FAFAF9] transition-colors p-4 flex items-center justify-between gap-4"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FileText className="w-4 h-4 text-[#9B9A97] shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">{s.url}</div>
                      <div className="text-xs text-[#9B9A97] mt-0.5">{new Date(s.created_at).toLocaleString()}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <ScorePill label="SEO" value={s.seo_score} />
                    <ScorePill label="GEO" value={s.geo_score} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`flex items-center gap-2 px-4 py-2.5 text-sm rounded-md transition-colors ${
        active ? "bg-[#F1F1EF] text-[#37352F] font-medium" : "text-[#787774] hover:bg-[#FAFAF9]"
      }`}
    >
      {children}
    </button>
  );
}

function ScorePill({ label, value }) {
  const color = value >= 80 ? "callout-green text-[#0F7B0F]" : value >= 60 ? "callout-orange text-[#B25E00]" : "callout-red text-[#B0261D]";
  return (
    <div className={`${color} rounded-md px-2.5 py-1 text-center`}>
      <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  );
}
